import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { estimatesAPI } from '../../api';
import { stripIframePreviewScripts } from '../../lib/stripIframePreviewScripts';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import {
  ArrowLeft,
  Bookmark,
  Check,
  HelpCircle,
  Link2,
  Mail,
  Pencil,
  Printer,
  X,
} from 'lucide-react';

function buildContactList(client, apiContacts) {
  const list = [];
  if (!client?.id) return list;

  if (client.email) {
    const clientType = client.clientType || client.client_type;
    const name =
      clientType === 'company'
        ? client.companyName || client.company_name || 'Primary contact'
        : `${client.firstName || client.first_name || ''} ${client.lastName || client.last_name || ''}`.trim() ||
          'Primary contact';
    list.push({
      id: 'primary',
      name,
      email: client.email,
      isPrimary: true,
    });
  }

  (apiContacts || []).forEach((contact) => {
    if (contact.email) {
      list.push({
        id: contact.id,
        name:
          `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email,
        email: contact.email,
        position: contact.position,
        isPrimary: false,
      });
    }
  });

  return list;
}

export default function EstimateSend() {
  const { id } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [html, setHtml] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [availableContacts, setAvailableContacts] = useState([]);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [sendEmailAddresses, setSendEmailAddresses] = useState([]);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [sendEmailSuccess, setSendEmailSuccess] = useState('');
  const [copyLinkState, setCopyLinkState] = useState('idle');

  const [flagSentOpen, setFlagSentOpen] = useState(false);
  const [flagSentLoading, setFlagSentLoading] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [htmlDoc, est] = await Promise.all([
        estimatesAPI.getHtml(id, { embed: true }),
        estimatesAPI.getOne(id),
      ]);
      setHtml(stripIframePreviewScripts(typeof htmlDoc === 'string' ? htmlDoc : ''));
      setEstimate(est);
    } catch (e) {
      setError(e.message || 'Failed to load estimate');
      setHtml('');
      setEstimate(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const refreshEstimateData = useCallback(async () => {
    if (!id) return;
    try {
      const [htmlDoc, est] = await Promise.all([
        estimatesAPI.getHtml(id, { embed: true }),
        estimatesAPI.getOne(id),
      ]);
      setHtml(stripIframePreviewScripts(typeof htmlDoc === 'string' ? htmlDoc : ''));
      setEstimate(est);
    } catch (e) {
      setError(e.message || 'Failed to refresh estimate');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshContacts = useCallback(async (client) => {
    if (!client?.id) {
      setAvailableContacts([]);
      return;
    }
    try {
      const response = await api.get(`/clients/${client.id}/contacts`);
      const contacts = response.data.contacts || [];
      setAvailableContacts(buildContactList(client, contacts));
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setAvailableContacts(buildContactList(client, []));
    }
  }, []);

  useEffect(() => {
    if (estimate?.client) {
      refreshContacts(estimate.client);
    } else {
      setAvailableContacts([]);
    }
  }, [estimate?.client, refreshContacts]);

  const openEmailModal = () => {
    setSendEmailSuccess('');
    setError('');
    const primary = availableContacts.find((c) => c.isPrimary);
    if (primary) {
      setSendEmailAddresses([primary.email]);
    } else if (availableContacts[0]?.email) {
      setSendEmailAddresses([availableContacts[0].email]);
    } else {
      setSendEmailAddresses([]);
    }
    setSendEmailModalOpen(true);
  };

  const toggleRecipientEmail = (email) => {
    setSendEmailAddresses((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.focus();
      win.print();
    }
  };

  const handleSendEmail = async () => {
    if (!id || sendEmailAddresses.length === 0) {
      setError('Please select at least one recipient');
      return;
    }
    try {
      setSendEmailLoading(true);
      setError('');
      setSendEmailSuccess('');
      for (let i = 0; i < sendEmailAddresses.length; i++) {
        const em = sendEmailAddresses[i];
        await estimatesAPI.sendEmail(id, em.trim(), 30);
      }
      setSendEmailSuccess(`Estimate link sent to ${sendEmailAddresses.length} recipient(s).`);
      setSendEmailAddresses([]);
      await refreshEstimateData();
      setTimeout(() => {
        setSendEmailModalOpen(false);
        setSendEmailSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to send estimate email');
    } finally {
      setSendEmailLoading(false);
    }
  };

  const handleFlagSent = async () => {
    if (!id) return;
    try {
      setFlagSentLoading(true);
      setError('');
      await estimatesAPI.send(id);
      setFlagSentOpen(false);
      await refreshEstimateData();
    } catch (err) {
      setError(err.message || 'Could not update estimate status');
    } finally {
      setFlagSentLoading(false);
    }
  };

  const handleCopyEstimateLink = async () => {
    if (!id) return;
    try {
      setError('');
      const result = await estimatesAPI.generateLink(id, 30);
      const url = `${window.location.origin}/portal/estimate/${result.token}`;
      await navigator.clipboard.writeText(url);
      setCopyLinkState('copied');
      setTimeout(() => setCopyLinkState('idle'), 2500);
    } catch (err) {
      setError(err.message || 'Could not copy link');
    }
  };

  const canFlagSent = ['draft', 'queried'].includes((estimate?.status ?? '').toLowerCase());
  const deleted = estimate?.isDeleted;

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold truncate">
                {estimate?.estimateNum ? `Send estimate ${estimate.estimateNum}` : 'Send estimate'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Choose how to deliver this estimate, then review the preview below
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tenant/estimates/${id}`)}
                disabled={!id || deleted}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Modify estimate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={!html || loading}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)]"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/tenant/estimates')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to list
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        {deleted && (
          <div className="p-4 bg-[var(--ds-control-field-bg)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)]">
            <p className="text-sm text-[var(--ds-text-secondary)]">
              This estimate was deleted. You can return to the estimate list to manage active estimates.
            </p>
          </div>
        )}

        {!deleted && (
          <Card className="overflow-hidden bg-[var(--ds-surface-panel)] border-[var(--ds-border-subtle)]">
            <CardContent className="p-0">
              <ul className="divide-y divide-[var(--ds-border-divider)]">
                <li>
                  <button
                    type="button"
                    onClick={openEmailModal}
                    disabled={availableContacts.length === 0}
                    className="flex w-full items-start gap-4 px-4 py-4 sm:px-6 text-left transition-colors hover:bg-[var(--ds-control-field-bg)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Mail className="h-8 w-8 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-semibold text-[var(--ds-accent-primary)]">
                        Send estimate by email
                      </span>
                      <p className="mt-1 text-xs text-[var(--ds-text-muted)]">
                        Email a secure link so your client can view and download the estimate (uses your tenant
                        client-email template).
                      </p>
                    </div>
                    <span title="Opens the recipient picker (secure link; optional CC to yourself).">
                      <HelpCircle className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                    </span>
                  </button>
                </li>
                {canFlagSent && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setFlagSentOpen(true)}
                      disabled={flagSentLoading}
                      className="flex w-full items-start gap-4 px-4 py-4 sm:px-6 text-left transition-colors hover:bg-[var(--ds-control-field-bg)] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Bookmark className="h-8 w-8 shrink-0 text-[var(--ds-status-danger)]" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-[var(--ds-accent-primary)]">
                          Flag estimate as sent
                        </span>
                        <p className="mt-1 text-xs text-[var(--ds-text-muted)]">
                          Updates your records only: marks the estimate as sent (issued) without sending email.
                          Available for draft estimates, or after a client has queried the quote.
                        </p>
                      </div>
                      <span title="Draft or queried estimates.">
                        <HelpCircle className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                      </span>
                    </button>
                  </li>
                )}
              </ul>
            </CardContent>
          </Card>
        )}

        <Card className="overflow-hidden bg-[var(--ds-surface-panel)] border-[var(--ds-border-subtle)]">
          <CardContent className="p-0">
            <div className="px-4 py-3 sm:px-6 border-b border-[var(--ds-border-divider)] bg-[var(--ds-control-field-bg)]">
              <h2 className="text-sm font-semibold text-[var(--ds-text-primary)]">Estimate preview</h2>
              <p className="text-xs text-[var(--ds-text-muted)] mt-0.5">Same layout as print and client view</p>
            </div>
            {loading ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]" />
                <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading preview…</p>
              </div>
            ) : html ? (
              <iframe
                ref={iframeRef}
                title={estimate?.estimateNum ? `Estimate ${estimate.estimateNum}` : 'Estimate preview'}
                srcDoc={html}
                sandbox="allow-same-origin allow-modals"
                className="w-full min-h-[85vh] border-0 bg-white block"
              />
            ) : (
              <p className="p-8 text-sm text-[var(--ds-text-muted)] text-center">No preview available.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {sendEmailModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] max-w-lg w-full my-8 p-4 sm:p-5 shadow-xl border border-[var(--ds-border-subtle)] text-[12px] leading-normal">
            <div className="flex items-center justify-between mb-4 gap-2">
              <h2 className="text-xs font-bold leading-tight text-gray-900 dark:text-gray-100">
                Send estimate by email
              </h2>
              <button
                type="button"
                onClick={() => {
                  setSendEmailModalOpen(false);
                  setSendEmailAddresses([]);
                  setSendEmailSuccess('');
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-0.5 rounded-md shrink-0"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {sendEmailSuccess ? (
              <div className="py-1">
                <div className="bg-[var(--ds-status-success)]/10 border border-[var(--ds-status-success)]/30 rounded-[var(--ds-radius-md)] p-3">
                  <p className="text-xs text-[var(--ds-status-success)]">{sendEmailSuccess}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-semibold text-[var(--ds-text-primary)]">Send to</span>
                  <div className="mt-2">
                    {availableContacts.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic leading-snug">
                      No email addresses on file for this client. Add contacts in the client record.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {availableContacts.map((contact) => {
                        const selected = sendEmailAddresses.includes(contact.email);
                        return (
                          <button
                            key={contact.id}
                            type="button"
                            onClick={() => toggleRecipientEmail(contact.email)}
                            className={`rounded-full px-2.5 py-1 text-xs font-medium leading-tight transition-colors border ${
                              selected
                                ? 'bg-emerald-600 border-emerald-600 text-white dark:bg-emerald-600 dark:border-emerald-600'
                                : 'bg-[var(--ds-control-field-bg)] border-[var(--ds-control-field-border)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)]/80'
                            }`}
                          >
                            {contact.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-1 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setSendEmailModalOpen(false);
                      setSendEmailAddresses([]);
                      setError('');
                    }}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleCopyEstimateLink()}
                    disabled={!id || loading}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {copyLinkState === 'copied' ? (
                      <>
                        <Check className="h-3.5 w-3.5 text-[var(--ds-status-success)] shrink-0" aria-hidden />
                        Link copied
                      </>
                    ) : (
                      <>
                        <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        Copy estimate link
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleSendEmail()}
                    disabled={sendEmailLoading || sendEmailAddresses.length === 0}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {sendEmailLoading ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {flagSentOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] max-w-md w-full p-6 shadow-xl border border-[var(--ds-border-subtle)]">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Mark estimate as sent?</h2>
            <p className="text-sm text-[var(--ds-text-secondary)] mb-6">
              This records the estimate as issued. It does not send an email by itself.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setFlagSentOpen(false)}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)]"
              >
                Cancel
              </Button>
              <Button variant="primary" size="sm" type="button" disabled={flagSentLoading} onClick={handleFlagSent}>
                {flagSentLoading ? 'Updating…' : 'Mark as sent'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
